/**
 * ActionExecutor — Handles suggest_intervention commands from the vision
 * loop server. Displays suggestions using the persistent chat pill or
 * card overlay depending on viewport visibility.
 *
 * Hybrid display strategy:
 *   - If the inline home card is visible → glass overlay on the card
 *   - Otherwise → suggestion appears in the persistent chat pill
 *
 * Flow:
 *   1. Server sends suggest_intervention → display via pill or card overlay
 *   2. User can: accept (click CTA), decline (click dismiss), ignore (auto-dismiss)
 *   3. Result sent back to server via action_complete
 *   4. If accepted, triggers conversation start with vision context
 */

import type { V1WsClient, InterventionMessage } from './v1WsClient'
import type { GlassChatPill } from '../ui/components/chatPill'
import { createGlassPanel, createGlassButton, glassText } from '../ui/glass'
import { springEnter, springExit, type SpringAnimation } from '../ui/spring'
import { injectTokens, tokens } from '../ui/tokens'

type WidgetStateGetter = () => string
type HomeVisibleGetter = () => boolean

const AUTO_DISMISS_MS = 15_000

export class ActionExecutor {
  private wsClient: V1WsClient
  private getWidgetState: WidgetStateGetter
  private getHomeVisible: HomeVisibleGetter
  private inlineTarget: HTMLElement | null
  private pill: GlassChatPill | null

  private currentIntervention: InterventionMessage | null = null
  /**
   * Most recently *ignored* intervention. Kept around so the post-ignore
   * pill (named-with-hint) can hover-to-recall: re-expand back into the
   * suggestion state with the same actionId. Cleared once the hint fades
   * (via the ActionExecutor's pill timer) or once another action arrives.
   */
  private recallableIntervention: InterventionMessage | null = null
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null
  private _active = false

  // Card overlay elements
  private cardOverlay: HTMLElement | null = null
  private cardOverlayAnim: SpringAnimation | null = null

  /** Called when an intervention is shown (before user responds). */
  onInterventionShown: ((goalId: string, message: string) => void) | null = null
  /** Called when the user accepts a suggestion. */
  onAccept: ((context: string) => void) | null = null

  private readonly disableAutoDismiss: boolean

  constructor(opts: {
    wsClient: V1WsClient
    getWidgetState: WidgetStateGetter
    getHomeVisible: HomeVisibleGetter
    inlineTarget?: HTMLElement | null
    pill?: GlassChatPill | null
    disableAutoDismiss?: boolean
  }) {
    this.wsClient = opts.wsClient
    this.getWidgetState = opts.getWidgetState
    this.getHomeVisible = opts.getHomeVisible
    this.inlineTarget = opts.inlineTarget ?? null
    this.pill = opts.pill ?? null
    this.disableAutoDismiss = opts.disableAutoDismiss ?? false

    this.wsClient.onIntervention = (msg) => this.handleIntervention(msg)
  }

  get isActive(): boolean {
    return this._active
  }

  destroy(): void {
    this.cleanup()
    this.wsClient.onIntervention = null
  }

  // -----------------------------------------------------------------------
  // Intervention handling
  // -----------------------------------------------------------------------

  private handleIntervention(msg: InterventionMessage): void {
    const state = this.getWidgetState()
    if (state === 'active' || state === 'connecting') {
      this.wsClient.sendActionComplete(msg.actionId, 'ignored')
      return
    }

    if (this._active) return

    console.log(`[ActionExecutor] Showing suggestion: "${msg.message}"`)
    this.currentIntervention = msg
    this._active = true
    this.onInterventionShown?.(msg.goalId, msg.message)

    const homeVisible = this.getHomeVisible()

    if (this.inlineTarget && homeVisible) {
      this.showCardOverlay(msg.message)
    } else if (this.pill) {
      this.showInPill(msg.message)
    }

    // Skip auto-dismiss in eval mode so the runner can accept even after a
    // long behavior sequence finishes. Real visitors have the 15s window.
    if (!this.disableAutoDismiss) {
      this.autoDismissTimer = setTimeout(() => this.handleIgnore(), AUTO_DISMISS_MS)
    }
  }

  // -----------------------------------------------------------------------
  // Display mode A: Card overlay (inline home is visible)
  // -----------------------------------------------------------------------

  private showCardOverlay(message: string): void {
    if (!this.inlineTarget) return
    injectTokens()

    this.inlineTarget.querySelector('.hs-suggestion-overlay')?.remove()

    this.cardOverlay = createGlassPanel({
      radius: 'lg',
      elevation: 'solid',
      className: 'hs-suggestion-overlay',
    })

    Object.assign(this.cardOverlay.style, {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: tokens.space4,
      boxSizing: 'border-box',
      zIndex: '10',
      textAlign: 'center',
      opacity: '0',
    })
    this.cardOverlay.setAttribute('data-hs-theme', 'dark')

    const msgEl = document.createElement('p')
    glassText(msgEl, 'primary', 'base', 'normal')
    Object.assign(msgEl.style, { margin: `0 0 ${tokens.space4} 0`, maxWidth: '260px' })
    msgEl.textContent = message
    this.cardOverlay.appendChild(msgEl)

    const actions = document.createElement('div')
    Object.assign(actions.style, { display: 'flex', gap: tokens.space3 })

    actions.appendChild(createGlassButton({
      label: "Let's talk",
      variant: 'accent',
      size: 'md',
      onClick: () => this.handleAccept(),
    }))

    actions.appendChild(createGlassButton({
      label: 'Not now',
      variant: 'ghost',
      size: 'sm',
      onClick: () => this.handleDecline(),
    }))

    this.cardOverlay.appendChild(actions)
    this.inlineTarget.style.position = 'relative'
    this.inlineTarget.appendChild(this.cardOverlay)

    this.cardOverlayAnim = springEnter(this.cardOverlay, {
      preset: 'snappy',
      from: { opacity: 0, scale: 0.96, y: 8 },
    })
  }

  private hideCardOverlay(): void {
    this.cardOverlayAnim?.stop()
    if (this.cardOverlay) {
      const el = this.cardOverlay
      this.cardOverlay = null
      this.cardOverlayAnim = springExit(el, { remove: true, preset: 'smooth' })
    }
  }

  // -----------------------------------------------------------------------
  // Display mode B: Suggestion in the persistent pill
  // -----------------------------------------------------------------------

  private showInPill(message: string): void {
    if (!this.pill) return

    this.pill.showSuggestion(message)
    this.pill.onSuggestionAccept = () => this.handleAccept()
    this.pill.onSuggestionDismiss = () => this.handleDecline()
  }

  // -----------------------------------------------------------------------
  // Response handlers
  // -----------------------------------------------------------------------

  /** Public method for programmatic acceptance (used in eval mode). */
  accept(): void {
    this.handleAccept()
  }

  private handleAccept(): void {
    if (!this._active || !this.currentIntervention) return
    console.log('[ActionExecutor] User accepted suggestion')

    const intervention = this.currentIntervention
    const context = `${intervention.context}\n\nThe user accepted your suggestion: "${intervention.message}"\nContinue the conversation naturally from this context.`
    const actionId = intervention.actionId

    this.cleanup({ pillTarget: 'named' })
    this.wsClient.sendActionComplete(actionId, 'accepted')
    this.onAccept?.(context)
  }

  private handleDecline(): void {
    if (!this._active || !this.currentIntervention) return
    console.log('[ActionExecutor] User declined suggestion')

    const actionId = this.currentIntervention.actionId
    // Explicit decline → bare named (no hint, no recall). Strong signal
    // that the visitor doesn't want this; resurfacing reads as harassment.
    this.recallableIntervention = null
    this.cleanup({ pillTarget: 'named' })
    this.wsClient.sendActionComplete(actionId, 'declined')
  }

  private handleIgnore(): void {
    if (!this._active || !this.currentIntervention) return
    console.log('[ActionExecutor] Suggestion auto-dismissed (ignored)')

    const intervention = this.currentIntervention
    // Stash for hover-to-recall before cleanup nulls currentIntervention.
    this.recallableIntervention = intervention
    this.cleanup({ pillTarget: 'named-with-hint' })
    this.wsClient.sendActionComplete(intervention.actionId, 'ignored')
  }

  /**
   * Hover-to-recall handler — invoked by the pill while the dismissed-hint
   * caption is showing. Re-expands the original suggestion with the same
   * actionId. If the visitor then clicks "Let's talk", the late accept
   * overrides the prior `ignored` outcome on the server (suppression
   * keeps last-write-wins per skill).
   */
  private recallIgnoredIntervention(): void {
    const intervention = this.recallableIntervention
    if (!intervention) {
      console.log('[ActionExecutor] Hover-recall skipped — no recallableIntervention')
      return
    }
    if (this._active) {
      console.log('[ActionExecutor] Hover-recall skipped — _active=true (a fresh action took over)')
      return
    }
    console.log(`[ActionExecutor] Hover-recall: re-showing "${intervention.message.slice(0, 60)}…"`)

    this.currentIntervention = intervention
    this._active = true
    this.recallableIntervention = null

    // Skip onInterventionShown — we don't want to double-count this as a
    // fresh fire in eval mode; it's a re-surface of the prior one.

    if (this.pill) {
      this.showInPill(intervention.message)
    }
    if (!this.disableAutoDismiss) {
      this.autoDismissTimer = setTimeout(() => this.handleIgnore(), AUTO_DISMISS_MS)
    }
  }

  // -----------------------------------------------------------------------
  // Cleanup
  // -----------------------------------------------------------------------

  /**
   * Tear down the active-state UI and decide what to leave behind on
   * the pill. Three targets:
   *   - `named`            — bare avatar + staff name. Used after accept
   *                          (about to connect) and after explicit decline
   *                          (visitor said "no", honor it).
   *   - `named-with-hint`  — avatar + name + dismissed-hint caption that
   *                          fades after ~40s; hovering it re-expands the
   *                          original suggestion. Used after *ignore*
   *                          (timeout) when the skill authored a hint.
   *   - `ambient`          — small avatar-only circle. Currently unused
   *                          on dismissal paths; reserved for future
   *                          quiet-hours / boot flows.
   */
  private cleanup(
    opts: { pillTarget: 'named' | 'named-with-hint' | 'ambient' } = { pillTarget: 'named' },
  ): void {
    this._active = false

    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer)
      this.autoDismissTimer = null
    }

    this.hideCardOverlay()
    if (this.pill) {
      this.pill.clearSuggestion()
      this.pill.onSuggestionAccept = null
      this.pill.onSuggestionDismiss = null
      if (opts.pillTarget === 'ambient') {
        this.pill.showAmbient()
      } else if (opts.pillTarget === 'named-with-hint') {
        const hint = this.recallableIntervention?.dismissedHint
        if (hint) {
          this.pill.showNamedWithHint(hint, () => this.recallIgnoredIntervention())
        }
        // If no hint authored, the pill is already in plain `named` after
        // clearSuggestion() — nothing more to do.
      }
    }
    this.currentIntervention = null
  }
}
